import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Send, Loader2, CheckCircle2 } from "lucide-react";

export const TelegramSettings = ({ client, API }) => {
  const [form, setForm] = useState({ bot_token: "", chat_id: "", enabled: false, send_hour: 21 });
  const [hasToken, setHasToken] = useState(false);
  const [masked, setMasked] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    client.get(`${API}/admin/settings/telegram`).then((r) => {
      setForm({ bot_token: "", chat_id: r.data.chat_id || "", enabled: r.data.enabled || false, send_hour: r.data.send_hour ?? 21 });
      setHasToken(r.data.has_token);
      setMasked(r.data.bot_token_masked || "");
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await client.put(`${API}/admin/settings/telegram`, { ...form, send_hour: Number(form.send_hour) });
      toast.success("Pengaturan Telegram disimpan");
      if (form.bot_token) { setHasToken(true); setMasked(`${form.bot_token.slice(0, 6)}...${form.bot_token.slice(-4)}`); setForm((f) => ({ ...f, bot_token: "" })); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan pengaturan");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await client.post(`${API}/admin/settings/telegram/test`);
      toast.success("Pesan uji terkirim! Cek Telegram Anda");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengirim pesan uji");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-16" data-testid="telegram-settings">
      <h2 className="font-display text-2xl tracking-tight flex items-center gap-3 mb-6">
        <Send className="w-5 h-5 text-[#A67C1E]" /> Notifikasi Klik Harian — Telegram
      </h2>
      <div className="border border-[#211B10]/10 bg-white p-8 grid md:grid-cols-2 gap-10">
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="tg-token" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">
              Bot Token {hasToken && <span className="inline-flex items-center gap-1 text-[#1B4332] normal-case tracking-normal ml-2"><CheckCircle2 className="w-3 h-3" /> tersimpan ({masked})</span>}
            </Label>
            <Input id="tg-token" data-testid="telegram-token-input" type="password" value={form.bot_token} onChange={(e) => setForm((f) => ({ ...f, bot_token: e.target.value }))} placeholder={hasToken ? "Isi hanya jika ingin mengganti token" : "123456:ABC-DEF..."} className="bg-[#FBF7EE] border-[#211B10]/15" />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="tg-chat" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Chat ID</Label>
              <Input id="tg-chat" data-testid="telegram-chatid-input" value={form.chat_id} onChange={(e) => setForm((f) => ({ ...f, chat_id: e.target.value }))} placeholder="cth: 123456789" className="bg-[#FBF7EE] border-[#211B10]/15" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tg-hour" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Jam Kirim (WIB)</Label>
              <Input id="tg-hour" data-testid="telegram-hour-input" type="number" min={0} max={23} value={form.send_hour} onChange={(e) => setForm((f) => ({ ...f, send_hour: e.target.value }))} className="bg-[#FBF7EE] border-[#211B10]/15" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch id="tg-enabled" data-testid="telegram-enabled-switch" checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />
            <Label htmlFor="tg-enabled" className="text-xs uppercase tracking-[0.15em] text-[#211B10]/50">Aktifkan laporan harian otomatis</Label>
          </div>
          <div className="flex gap-3 pt-2">
            <Button data-testid="telegram-save-button" onClick={save} disabled={saving} className="bg-[#A67C1E] text-[#FBF7EE] hover:bg-[#211B10] font-medium">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
            </Button>
            <Button data-testid="telegram-test-button" variant="outline" onClick={sendTest} disabled={testing || !hasToken} className="border-[#A67C1E]/50 text-[#A67C1E] bg-transparent hover:bg-[#A67C1E]/10">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Kirim Uji
            </Button>
          </div>
        </div>
        <div className="border-l-0 md:border-l border-[#211B10]/10 md:pl-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A67C1E] mb-4">Cara Setup (3 menit)</p>
          <ol className="space-y-3 text-sm text-[#211B10]/70 leading-relaxed list-decimal list-inside">
            <li>Buka Telegram, cari <span className="font-medium text-[#211B10]">@BotFather</span>, kirim <code className="bg-[#FBF7EE] px-1.5 py-0.5 text-xs">/newbot</code> dan ikuti instruksinya untuk mendapatkan <span className="font-medium">Bot Token</span>.</li>
            <li>Cari bot Anda yang baru dibuat, lalu kirim pesan apa saja (mis. "halo") agar bot bisa membalas Anda.</li>
            <li>Cari <span className="font-medium text-[#211B10]">@userinfobot</span>, kirim <code className="bg-[#FBF7EE] px-1.5 py-0.5 text-xs">/start</code> — angka Id yang muncul adalah <span className="font-medium">Chat ID</span> Anda.</li>
            <li>Tempel keduanya di sini, simpan, lalu klik <span className="font-medium">Kirim Uji</span>.</li>
          </ol>
        </div>
      </div>
    </div>
  );
};
